import { cognito } from '../config/auth';

export interface CognitoTokens {
    access_token: string;
    id_token: string;
    refresh_token?: string;
    token_type: string;
    expires_in: number;
}

interface StoredTokens {
    accessToken: string;
    idToken: string;
    refreshToken: string;
}

interface User {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    groups: string[];
}

const TOKEN_KEYS = {
    ACCESS_TOKEN: 'expat_access_token',
    ID_TOKEN: 'expat_id_token',
    REFRESH_TOKEN: 'expat_refresh_token',
} as const;

export function storeTokens(tokens: CognitoTokens): void {
    localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, tokens.access_token);
    localStorage.setItem(TOKEN_KEYS.ID_TOKEN, tokens.id_token);
    if (tokens.refresh_token) {
        localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, tokens.refresh_token);
    }
}

export function getStoredTokens(): StoredTokens | null {
    const accessToken = localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
    const idToken = localStorage.getItem(TOKEN_KEYS.ID_TOKEN);
    const refreshToken = localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);

    if (!accessToken || !idToken || !refreshToken) return null;
    return { accessToken, idToken, refreshToken };
}

export function clearTokens(): void {
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.ID_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
}

export function decodeJwt<T = Record<string, unknown>>(token: string): T {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as T;
}

export function isTokenExpired(token: string): boolean {
    try {
        const payload = decodeJwt<{ exp: number }>(token);
        return Date.now() >= payload.exp * 1000;
    } catch {
        return true;
    }
}

export function isTokenExpiringSoon(token: string, minutes = 5): boolean {
    try {
        const payload = decodeJwt<{ exp: number }>(token);
        return Date.now() >= payload.exp * 1000 - minutes * 60 * 1000;
    } catch {
        return true;
    }
}

export function getTokenExpirationTime(token: string): number {
    const payload = decodeJwt<{ exp: number }>(token);
    return payload.exp * 1000;
}

interface CognitoIdTokenPayload {
    sub: string;
    email: string;
    given_name?: string;
    family_name?: string;
    'cognito:username': string;
    'cognito:groups'?: string[];
}

export function extractUserFromIdToken(idToken: string): User | null {
    try {
        const payload = decodeJwt<CognitoIdTokenPayload>(idToken);
        return {
            id: payload.sub,
            email: payload.email,
            firstName: payload.given_name,
            lastName: payload.family_name,
            groups: payload['cognito:groups'] || [],
        };
    } catch {
        return null;
    }
}

export async function getFreshAccessToken(): Promise<string | null> {
    const tokens = getStoredTokens();
    if (!tokens) return null;

    if (!isTokenExpiringSoon(tokens.accessToken, 2)) {
        return tokens.accessToken;
    }

    try {
        const tokenEndpoint = `https://${cognito.domain}/oauth2/token`;
        const body = new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: cognito.userPoolWebClientId,
            refresh_token: tokens.refreshToken,
        });

        const response = await fetch(tokenEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body,
        });

        if (!response.ok) return null;

        const newTokens: CognitoTokens = await response.json();
        storeTokens(newTokens);
        return newTokens.access_token;
    } catch {
        return null;
    }
}

export type { User, StoredTokens };
